/**
 * Résolution des pages du catalogue à partir de l'URL :
 *   /<metier>                      → accueil du métier (ouvert à tous, modules lisibles)
 *   /<metier>/<module>[/<sous>]    → écran enregistré dans web/src/metiers/registre.ts, sinon squelette « Écran à développer »
 *   /referentiels/<dimension>, /pilotage/indicateurs → idem
 * Nœud absent du catalogue reçu (module non activé pour la filiale, ou sans droit) → « Module non disponible » ;
 * nœud présent mais non lisible → « Accès non autorisé ».
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ACTIONS_NOEUD, DIMENSIONS, cleLibre, noeudDeRoute, type MetierId, type NoeudCatalogue } from '@agora/shared';
import { Button, ModuleTag, QuickLinks, RecordSkeleton, StatusBadge, type QuickLinksProps } from '../../design';
import { BadgePortee, useT, type Traduire } from '../../socle';
import { useSocle } from '../contexte';
import { ECRANS, type Fiche } from '../../metiers/registre';

function libelleDroits(n: NoeudCatalogue, t: Traduire): string {
  if (n.droits.creer || n.droits.modifier) return t('metier.lectureEcriture');
  if (n.droits.lire) return t('metier.lectureSeule');
  return t('metier.aucunAcces');
}

function tuiles(liste: NoeudCatalogue[], metier: MetierId, t: Traduire, lib: (id: string) => string): QuickLinksProps['items'] {
  return liste.map((n) => ({
    id: n.id, label: lib(n.id), icon: n.icone, metier, href: n.route,
    path: `${n.statut === 'disponible' ? t('metier.disponible') : t('metier.aDevelopper')} · ${libelleDroits(n, t)}`,
  }));
}

/** Page en cours de chargement (catalogue, code de l'écran) : squelette d'une page – barre de titre et cartes. */
export function Chargement() {
  const { t } = useT();
  return <RecordSkeleton label={t('app.chargement')} />;
}

export function Introuvable() {
  const { t } = useT();
  const navigate = useNavigate();
  return (
    <>
      <div className="ag-pagehead"><div><h1>{t('erreurs.pageIntrouvable')}</h1></div></div>
      <section className="ag-glass-card agx-carte">
        <StatusBadge status="neutral" label="404" />
        <div><Button variant="primary" icon="home" onClick={() => navigate('/')}>{t('nav.accueil')}</Button></div>
      </section>
    </>
  );
}

export function NonAutorise({ titre }: { titre: string }) {
  const { t } = useT();
  return (
    <>
      <div className="ag-pagehead"><div><h1>{titre}</h1></div></div>
      <section className="ag-glass-card agx-carte" role="alert">
        <StatusBadge status="danger" label={t('module.nonAutoriseTitre')} />
        <p className="agx-texte">{t('module.nonAutoriseTexte')}</p>
      </section>
    </>
  );
}

/** Nœud du catalogue absent pour l'utilisateur dans la filiale courante (non activé ou sans droit) : message neutre. */
export function NonDisponible({ titre }: { titre: string }) {
  const { t } = useT();
  return (
    <>
      <div className="ag-pagehead"><div><h1>{titre}</h1></div></div>
      <section className="ag-glass-card agx-carte" role="status">
        <StatusBadge status="neutral" label={t('module.nonDisponibleTitre')} />
        <p className="agx-texte">{t('module.nonDisponibleTexte')}</p>
      </section>
    </>
  );
}

/** Squelette d'un écran pas encore développé : statut, droits de l'utilisateur, sous-modules, guide. */
export function EcranADevelopper({ noeud }: { noeud: NoeudCatalogue }) {
  const { t, lib } = useT();
  const metier = (noeud.metier || DIMENSIONS.find((d) => d.id === noeud.id)?.proprietaire || 'it') as MetierId;
  return (
    <>
      <div className="ag-pagehead"><div><h1>{lib(noeud.id)}</h1></div></div>
      <section className="ag-glass-card agx-carte">
        <div className="agx-ligne-badges">
          <StatusBadge status="waiting" label={t('module.aDevelopperTitre')} />
          {noeud.type === 'Dimension' ? <ModuleTag metier={metier} label={t('dim.proprietaire', { metier: lib(metier) })} /> : null}
          {noeud.type === 'Dimension' ? <BadgePortee id={noeud.id} /> : null}
        </div>
        <p className="agx-texte">{t('module.aDevelopperTexte')}</p>
        <h2 className="agx-titre-carte">{t('module.vosDroits')}</h2>
        <div className="agx-ligne-badges">
          {ACTIONS_NOEUD.map((a) => (
            <StatusBadge key={a} status={noeud.droits[a] ? 'success' : 'neutral'} label={t(cleLibre('droits', a))} />
          ))}
        </div>
        <p className="agx-vide ag-mono">{t('module.guide')}</p>
      </section>
      {noeud.enfants.length ? (
        <section className="ag-glass-card agx-carte">
          <h2 className="agx-titre-carte">{t('module.sousModules')}</h2>
          <QuickLinks items={tuiles(noeud.enfants, metier, t, lib)} />
        </section>
      ) : null}
    </>
  );
}

/** Accueil d'un métier : visible par tous les collaborateurs, modules selon les droits. */
export function AccueilMetier({ noeud }: { noeud: NoeudCatalogue }) {
  const { t, lib } = useT();
  const metier = noeud.id as MetierId;
  return (
    <>
      <div className="ag-pagehead">
        <div>
          <h1>{lib(noeud.id)}</h1>
          <p className="agx-sous-titre">{t('metier.presentation', { metier: lib(noeud.id) })}</p>
        </div>
      </div>
      <section className="ag-glass-card agx-carte">
        <h2 className="agx-titre-carte">{t('metier.modules')}</h2>
        {noeud.enfants.length ? <QuickLinks items={tuiles(noeud.enfants, metier, t, lib)} /> : <p className="agx-vide">{t('metier.aucunModule')}</p>}
      </section>
    </>
  );
}

/** Fiche d'un objet (route déclarée dans FICHES) : mêmes contrôles que l'écran du nœud qui la porte. */
export function PageFiche({ fiche }: { fiche: Fiche }) {
  const { lib } = useT();
  const { catalogue, noeuds } = useSocle();
  if (!catalogue) return <Chargement />;
  const nc = noeuds.get(fiche.noeud);
  if (!nc) return <NonDisponible titre={lib(fiche.noeud)} />;
  if (!nc.droits.lire) return <NonAutorise titre={lib(fiche.noeud)} />;
  const Ecran = fiche.ecran;
  return (
    <React.Suspense fallback={<Chargement />}>
      <Ecran noeud={nc} />
    </React.Suspense>
  );
}

export function PageNoeud() {
  const { pathname } = useLocation();
  const { lib } = useT();
  const { catalogue, noeuds } = useSocle();
  const n = noeudDeRoute(pathname);
  if (!n) return <Introuvable />;
  if (!catalogue) return <Chargement />;
  const nc = noeuds.get(n.id);
  if (!nc) return <NonDisponible titre={lib(n.id)} />;
  if (n.type === 'Metier') return <AccueilMetier noeud={nc} />;
  const Ecran = ECRANS[n.id];
  if (!Ecran) return <EcranADevelopper noeud={nc} />;
  if (!nc.droits.lire) return <NonAutorise titre={lib(n.id)} />;
  return (
    <React.Suspense fallback={<Chargement />}>
      <Ecran noeud={nc} />
    </React.Suspense>
  );
}
